// @ts-nocheck
import {useEffect, useId, useRef} from "react";
import {useTranslation} from "react-i18next";
import LoadingLogo from "../../../components/LoadingLogo";
import {trackGameStart} from "../../../providers/analytics";
import useCurrencyStore from "../../../store/useCurrency.store";
import {useHorsesStore} from "../../../store/useHorses.store";
import "./Horses.css";

const HorsesScreen = () => {
  const {t} = useTranslation();

  const startHorseGame = useHorsesStore(state => state.startHorseGame);
  const gameUrl = useHorsesStore(state => state.gameUrl);
  const loading = useHorsesStore(state => state.loading);
  const unload = useHorsesStore(state => state.unload);
  const error = useHorsesStore(state => state.error);
  const screenMode = useHorsesStore(state => state.screenMode);
  const setScreenMode = useHorsesStore(state => state.setScreenMode);
  const resetScreenModeToDefault = useHorsesStore(state => state.resetScreenModeToDefault);

  const selectedCurrency = useCurrencyStore(state => state.selectedCurrency);

  useEffect(() => {
    const handleResize = () => {
      // Reset to default screen mode based on new screen size
      resetScreenModeToDefault();
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [resetScreenModeToDefault]);

  const iFrameRef = useRef(null);
  const iframeId = useId();

  useEffect(() => {
    return () => {
      unload();
      // Wait for the element to be available
      const bottomNavMenu = document.querySelector(".bottomNavMenu");
      if (bottomNavMenu) {
        bottomNavMenu.style.visibility = "visible";
      }
    };
  }, [unload]);

  const handleStartGame = type => {
    const currency = selectedCurrency?.id || "VES";
    startHorseGame(currency, type);
    trackGameStart("horses", "elinmejorable");

    // Force fullscreen mode for mobile when entering the game
    if (window.innerWidth <= 768) {
      setScreenMode("fullscreen");
    }
  };

  useEffect(() => {
    if (iFrameRef.current) {
      iFrameRef.current.focus(); // Set focus when the iframe loads
      iFrameRef.current.scrollIntoView({behavior: "smooth", block: "center"});
    }

    if (gameUrl) {
      if (screenMode === "fullscreen") {
        setTimeout(() => {
          window.scrollTo(0, 1);
        }, 100);
      } else {
        // For centered mode, scroll to center the iframe
        setTimeout(() => {
          if (iFrameRef.current) {
            iFrameRef.current.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }, 100);
      }
    }
  }, [gameUrl, screenMode]); // Trigger the effect when gameUrl or screenMode changes

  const headerHeight = document.querySelector("header")?.offsetHeight || 0; // Get header height

  // Handle bottom navigation visibility based on screen mode
  useEffect(() => {
    const bottomNavMenu = document.querySelector(".bottomNavMenu");
    if (bottomNavMenu) {
      if (screenMode === "fullscreen") {
        bottomNavMenu.style.visibility = "hidden";
      } else {
        bottomNavMenu.style.visibility = "visible";
      }
    }
  }, [screenMode, gameUrl]);

  const toggleScreenMode = () => {
    const newMode = screenMode === "fullscreen" ? "centered" : "fullscreen";
    setScreenMode(newMode);
  };

  return (
    <div
      className={`horses-container ${screenMode === "fullscreen" ? "fullscreen" : "centered"}`}
      style={{
        height: screenMode === "fullscreen" ? "100vh" : `calc(100vh - ${headerHeight}px)`,
      }}
    >
      {/* Back Button */}
      {gameUrl && (
        <button
          type="button"
          onClick={unload}
          className="horses-control-btn horses-back-btn"
        >
          <span>←</span>
          {t("backToSelection", "Volver")}
        </button>
      )}

      {/* Fullscreen Controls */}
      {screenMode !== "fullscreen" && window.innerWidth > 768 && (
        <button
          type="button"
          onClick={toggleScreenMode}
          className="horses-control-btn horses-fullscreen-btn"
        >
          {t("enterFullscreen", "Enter Fullscreen")}
        </button>
      )}
      {screenMode === "fullscreen" && (
        <button
          type="button"
          onClick={toggleScreenMode}
          className="horses-control-btn horses-fullscreen-btn"
        >
          {t("exitFullscreen", "Exit Fullscreen")}
        </button>
      )}

      {error && (
        <div className="horses-error-container">
          <h3>{t("gameLoadError", "We had an issue loading the horse racing game.")}</h3>
        </div>
      )}

      {loading ? (
        <LoadingLogo />
      ) : !gameUrl ? (
        <div className="horses-selection-container">
          <div className="horses-header">
            <span className="horses-sport-badge">
              <span className="horses-sport-badge-dot" />
              Hipismo
            </span>
            <h2 className="horses-selection-title">{t("selectRaceType", "Selecciona el tipo de carrera")}</h2>
            <p className="horses-selection-subtitle">{t("selectRaceTypeSubtitle", "Elige la categoría y comienza a jugar")}</p>
          </div>
          <div className="horses-buttons-wrapper">
            <button
              type="button"
              onClick={() => handleStartGame("national")}
              className="horses-selection-btn national"
            >
              <div className="horses-btn-icon-orb">🇻🇪</div>
              <div className="horses-btn-body">
                <span className="horses-btn-scope">Venezuela</span>
                <span className="horses-btn-text">{t("nationalRaces", "Nacionales")}</span>
                <span className="horses-btn-desc">{t("nationalRacesDesc", "Carreras locales en vivo")}</span>
              </div>
              <span className="horses-btn-arrow">→</span>
            </button>

            <button
              type="button"
              onClick={() => handleStartGame("international")}
              className="horses-selection-btn international"
            >
              <div className="horses-btn-icon-orb">🌎</div>
              <div className="horses-btn-body">
                <span className="horses-btn-scope">Mundial</span>
                <span className="horses-btn-text">{t("internationalRaces", "Internacionales")}</span>
                <span className="horses-btn-desc">{t("internationalRacesDesc", "Competencias del mundo")}</span>
              </div>
              <span className="horses-btn-arrow">→</span>
            </button>
          </div>
        </div>
      ) : (
        <iframe
          id={iframeId}
          ref={iFrameRef}
          src={gameUrl}
          frameBorder="0"
          title={t("horseRacingGame", "Horse Racing Game")}
          allow="clipboard-read; clipboard-write; fullscreen; screen-wake-lock; accelerometer; gyroscope; magnetometer; microphone; camera; geolocation"
          className="horses-iframe"
          style={{
            height: screenMode === "fullscreen" ? "100vh" : `calc(100vh - ${headerHeight}px)`,
          }}
        />
      )}
    </div>
  );
};

export default HorsesScreen;
